import TitleBar from '../../../components/TitleBar';
import { ColumnsType } from 'antd/es/table';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Footer } from '../../../components/common/Footer';
import { Button } from '../../../components';
import { FETCH_MITIGATION_BY_DIVISION } from '../queries';
import { useQuery } from '@apollo/client';
import FinancialData from '../../../components/common/FinancialData';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import { useUserContext } from '../../../context/PermissionsContext';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import QueryErrorBoundary from '../../../components/error-handling';
import { baseColumnsMitigation } from './exportColumns';
import TitleUpdater from '../../../components/common/TitleUpdater';

type MitigationCalculates = {
  uuid: string;
  locationName: string;
  rebuildTime: number;
  unmitigatedRevenueExposed: number;
  availableInternalMitigation: number;
  availableExternalMitigation: number;
  combinedMitigation: number;
  mitigationPercentage: number;
  netRevenueExposure: number;
  availableFgMitigation: number;
  level: number;
};

interface DataReturned {
  mitigationCalculates: MitigationCalculates[];
}

function AutomotiveDivision() {
  const navigate = useNavigate();
  const params = useParams();
  let [searchParams] = useSearchParams();
  const divisionName = searchParams.get('division')!;
  const divisionId = params.divisionId!;

  const { selectedTags, isLocation } = useUserContext();

  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_MITIGATION_BY_DIVISION,
    {
      variables: {
        division: { uuid: divisionId },
      },
    },
  );

  const filteredLocations = getFilteredTags({
    data: data?.mitigationCalculates || [],
    selectedTags,
    condition: isLocation,
    lookUpKey: 'uuid',
  });

  const columns: ColumnsType<MitigationCalculates> = [
    {
      title: 'Location',
      dataIndex: 'locationName',
      key: 'locationName',
      align: 'left',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationName, b.locationName),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Rebuild Time (months)',
      dataIndex: 'rebuildTime',
      key: 'rebuildTime',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.rebuildTime, b.rebuildTime),
      render: (text: number) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Unmitigated Revenue Exposed',
      dataIndex: 'unmitigatedRevenueExposed',
      key: 'unmitigatedRevenueExposed',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.unmitigatedRevenueExposed,
          b.unmitigatedRevenueExposed,
        ),
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    {
      title: 'Finished Goods Mitigation',
      dataIndex: 'availableFgMitigation',
      key: 'availableFgMitigation',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.availableFgMitigation,
          b.availableFgMitigation,
        ),
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    {
      title: 'Available Internal Mitigation',
      dataIndex: 'availableInternalMitigation',
      key: 'availableInternalMitigation',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.availableInternalMitigation,
          b.availableInternalMitigation,
        ),
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    {
      title: 'Available External Mitigation',
      dataIndex: 'availableExternalMitigation',
      key: 'availableExternalMitigation',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.availableExternalMitigation,
          b.availableExternalMitigation,
        ),
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    {
      title: 'Combined Mitigation',
      dataIndex: 'combinedMitigation',
      key: 'combinedMitigation',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.combinedMitigation, b.combinedMitigation),
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    {
      title: 'Mitigation %',
      dataIndex: 'mitigationPercentage',
      key: 'mitigationPercentage',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.mitigationPercentage,
          b.mitigationPercentage,
        ),
      render: (text: number) => (
        <span className="custom-table--column text-sm">
          {text}
          {'%'}
        </span>
      ),
    },
    {
      title: 'Net Revenue Exposure',
      dataIndex: 'netRevenueExposure',
      key: 'netRevenueExposure',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.netRevenueExposure, b.netRevenueExposure),
      render: (text: number) => (
        <span className="custom-table--column text-sm">
          {<FinancialData text={text} />}
        </span>
      ),
    },
  ];

  const extendActions = (record: any) => (
    <Button
      type="primary-rounded"
      className="text-sm min-w-[145px]"
      onClick={() =>
        navigate(
          `/mitigation/${divisionId}/location-selection?division=${divisionName}&locationId=${record.uuid}&locationName=${record.locationName}&level=${record.level}`,
        )
      }
    >
      Update Mitigation
    </Button>
  );

  return (
    <>
      <TitleUpdater title={`${divisionName} Division`} />
      <TitleBar
        title="Location Mitigation (Internal/External)"
        subtitle={`Location Selection`}
        brief="Beyond finished goods, mitigation can come from internal or external sources. Internal
                      mitigation involves moving production to an alternate location that your company owns. External
                      mitigation relies on non-owned resources such as contract manufacturers or even competitors."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title={`${divisionName}`}
          columns={columns}
          data={filteredLocations}
          loading={loading}
          extendActions={extendActions}
          exportExcelColumns={baseColumnsMitigation}
        />
      </QueryErrorBoundary>
      <Footer backPath={'/internal-external-mitigation'} />
    </>
  );
}

export default AutomotiveDivision;
